import React, { useMemo, useState } from 'react';

/**
 * The period control shared by every CRM Analytics tab (Agents, Overview,
 * Agent Usage) — 1D / 1W / 2W / 1M / 3M / 6M / 12M / FY / Custom, defaulting to the
 * current Australian financial year.
 *
 * These tabs deliberately do NOT use the page-wide date dropdown in the
 * Analytics header. That one is built for the buyer portal ("last 30 days",
 * "overall"), whereas agency performance is read by financial year. Rather than
 * copy this control into a third component, it lives here with its state in
 * `useAnalyticsPeriod`.
 *
 * The FY defaults to the current one and auto-rolls on 1 July, so nothing needs
 * redeploying at the turn of the financial year.
 */

// Week windows are plain trailing days; month windows snap to the 1st (see below).
// 1D = today only (CG, Monday 13058627428: "Please add a 1 day button").
export const QUICK_RANGES = [
    { key: '1d', label: '1D', days: 1 },
    { key: '1w', label: '1W', days: 7 },
    { key: '2w', label: '2W', days: 14 },
    { key: '1m', label: '1M', months: 1 },
    { key: '3m', label: '3M', months: 3 },
    { key: '6m', label: '6M', months: 6 },
    { key: '12m', label: '12M', months: 12 },
];

// Australian FY runs 1 Jul → 30 Jun; identified by its START year (Jul 2026 → FY 26-27).
export const fyStartYear = (d) => (d.getMonth() + 1 >= 7 ? d.getFullYear() : d.getFullYear() - 1);
export const fyLabel = (y) => `FY ${String(y).slice(-2)}-${String(y + 1).slice(-2)}`;
export const fyRange = (y) => ({ start: `${y}-07-01`, end: `${y + 1}-06-30` });
export const toYmd = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const useAnalyticsPeriod = () => {
    const currentFY = fyStartYear(new Date());
    const [rangeMode, setRangeMode] = useState('fy'); // fy | 1d | 1w | 2w | 1m | 3m | 6m | 12m | custom
    const [fyYear, setFyYear] = useState(currentFY);
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');

    // Financial years offered in the FY dropdown: current + the previous four.
    const fyOptions = useMemo(() => {
        const arr = [];
        for (let y = currentFY; y >= currentFY - 4; y--) arr.push(y);
        return arr;
    }, [currentFY]);

    // The active window (YYYY-MM-DD) sent to the analytics endpoints.
    const range = useMemo(() => {
        if (rangeMode === 'fy') return fyRange(fyYear);
        if (rangeMode === 'custom') {
            if (customStart && customEnd) return { start: customStart, end: customEnd };
            return fyRange(currentFY); // custom not fully picked yet — fall back
        }
        const end = new Date();
        const quick = QUICK_RANGES.find((r) => r.key === rangeMode) || {};
        // Week windows (1W/2W) are exact trailing days ending today — "the last 7 /
        // 14 days", no month snapping, since a calendar week has no month anchor.
        if (quick.days) {
            const start = new Date(end.getFullYear(), end.getMonth(), end.getDate() - (quick.days - 1));
            return { start: toYmd(start), end: toYmd(end) };
        }
        const monthsBack = quick.months || 3;
        // Snap the window start to the 1st of that month so "1M" covers the whole
        // of last month (not a mid-month trailing 30 days). Keeps 1M aligned with
        // FY while a financial year is only a month or two old, and avoids
        // day-of-month drift / Feb overflow.
        const start = new Date(end.getFullYear(), end.getMonth() - monthsBack, 1);
        return { start: toYmd(start), end: toYmd(end) };
    }, [rangeMode, fyYear, customStart, customEnd, currentFY]);

    return {
        range, currentFY, fyOptions,
        rangeMode, setRangeMode,
        fyYear, setFyYear,
        customStart, setCustomStart,
        customEnd, setCustomEnd,
    };
};

export const PeriodSelector = ({ period, align = 'end' }) => {
    const {
        rangeMode, setRangeMode, fyYear, setFyYear, fyOptions, currentFY,
        customStart, setCustomStart, customEnd, setCustomEnd,
    } = period;

    const pill = (active) =>
        `px-2.5 py-1 text-[11px] font-semibold rounded-full transition-colors ${
            active ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
        }`;

    return (
        <div className={`flex items-center justify-${align} gap-2 flex-wrap`}>
            <div className="inline-flex flex-wrap justify-end items-center bg-gray-100 rounded-full p-0.5 border border-gray-200">
                {QUICK_RANGES.map((r) => (
                    <button key={r.key} onClick={() => setRangeMode(r.key)} className={pill(rangeMode === r.key)}>
                        {r.label}
                    </button>
                ))}
                <button onClick={() => setRangeMode('fy')} className={pill(rangeMode === 'fy')}>FY</button>
                <button onClick={() => setRangeMode('custom')} className={pill(rangeMode === 'custom')}>Custom</button>
            </div>

            {rangeMode === 'fy' && (
                <select
                    value={fyYear}
                    onChange={(e) => setFyYear(Number(e.target.value))}
                    className="text-xs font-semibold text-gray-800 px-2 py-1.5 rounded-lg border border-gray-200 bg-white cursor-pointer focus:outline-none"
                >
                    {fyOptions.map((y) => (
                        <option key={y} value={y}>
                            {fyLabel(y)}{y === currentFY ? ' · Current' : ''}
                        </option>
                    ))}
                </select>
            )}

            {rangeMode === 'custom' && (
                <div className="flex items-center gap-1">
                    <input
                        type="date"
                        value={customStart}
                        max={customEnd || undefined}
                        onChange={(e) => setCustomStart(e.target.value)}
                        className="text-xs px-2 py-1 rounded-lg border border-gray-200 focus:outline-none"
                    />
                    <span className="text-[11px] text-gray-400">to</span>
                    <input
                        type="date"
                        value={customEnd}
                        min={customStart || undefined}
                        onChange={(e) => setCustomEnd(e.target.value)}
                        className="text-xs px-2 py-1 rounded-lg border border-gray-200 focus:outline-none"
                    />
                </div>
            )}
        </div>
    );
};

export default PeriodSelector;
